import { ReactNode } from 'react';

import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import { preDefinedKey } from '@features/transactions/constants';
import { formatISODate } from '@utils/date';

import {
  getTransactionHistoryKeys,
  getTransactionHistoryTitle
} from './transactionHistory';

export type PaymentHistoryDisplaySource = Record<string, unknown> & {
  payment_mode: string;
  paid_amount: string;
  payment_method?: string;
  payment_description?: string;
  type?: string;
  settlement_status?: string;
};

export type PaymentHistoryDisplayRow = {
  title: string;
  value: string | ReactNode;
  className: string;
};

/** Maps one payment / discount history entry to rows for `TransactionHistory`. */
export const buildPaymentHistoryDisplayRows = (
  paymentHistory: PaymentHistoryDisplaySource
): PaymentHistoryDisplayRow[] => {
  const keys = getTransactionHistoryKeys(
    paymentHistory.payment_mode,
    String(paymentHistory.paid_amount ?? ''),
    paymentHistory.type as string | undefined
  );

  const paymentMode = paymentHistory.payment_mode;
  const paymentMethod = String(paymentHistory.payment_method ?? '');
  const entryType = paymentHistory.type as string | undefined;

  const rows = keys.map((key) => {
    const cellValue = (): string | ReactNode => {
      if (key === preDefinedKey.paidAt) {
        return formatISODate({
          date: paymentHistory[key] as string,
          format:
            paymentMode === 'automatic' ||
            entryType === 'discount' ||
            entryType === 'closed'
              ? 'DD MMM YYYY at hh:mm am/pm'
              : 'DD MMM YYYY'
        });
      }
      if (
        key === preDefinedKey.markedPaidAt ||
        key === preDefinedKey.settledAt
      ) {
        return paymentHistory[key] !== 'null'
          ? formatISODate({
              date: paymentHistory[key] as string,
              format: 'DD MMM YYYY at hh:mm am/pm'
            })
          : DEFAULT_PLACEHOLDER;
      }
      if (key === preDefinedKey.paymentMode) {
        return getTransactionHistoryTitle(
          key,
          paymentMode,
          paymentMethod,
          entryType
        );
      }
      if (key === preDefinedKey.paymentDescription) {
        const note = String(paymentHistory.payment_description ?? '').trim();
        return note && note.toLowerCase() !== 'null' ? note : '';
      }
      return paymentHistory[key] as string;
    };

    const badgeClassName = () => {
      if (entryType === 'discount') {
        return 'bg-cofeeTeal1 !text-cofeeTeal5';
      }
      if (entryType === 'closed') {
        return '';
      }
      if (paymentHistory.settlement_status === 'settled') {
        return 'bg-primary10 !text-primary40 ';
      }
      return 'bg-blue !text-blue1';
    };

    const titleCell = () =>
      getTransactionHistoryTitle(key, paymentMode, paymentMethod, entryType);

    return {
      title: titleCell(),
      value: cellValue(),
      className:
        key === preDefinedKey.paymentMode
          ? `px-2.5 py-1 rounded-lg ${badgeClassName()}`
          : ''
    };
  });

  const badgeIndex = rows.findIndex((item) => item.className !== '');
  if (badgeIndex !== -1) {
    rows.splice(badgeIndex + 1, 0, {
      title: 'Amount',
      value: (
        <CurrencyAmount
          value={Number(paymentHistory.paid_amount)}
          className="gap-0.5"
          symbolClass=""
          valueClass=""
        />
      ),
      className: ''
    });
  }

  return rows;
};
